import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  analyticsModelsApi,
  type CreateAnalyticsModelRequest,
  type ListAnalyticsModelsParams,
  type UpdateAnalyticsModelRequest,
} from '@/shared/api/transcriptor/analyticsModelsApi';
import { SliceName } from '@/shared/config';

import {
  AnalyticsModel,
  AnalyticsModelSummary,
  VersionStatus,
} from '../config';

type AnalyticsModelState = {
  analyticsModels: AnalyticsModelSummary[];
  // Summaries carry no tags and no sections, so the editor reads the models it
  // opens from here, filled by fetchAnalyticsModelById.
  analyticsModelById: Record<string, AnalyticsModel>;
  analyticsModelByIdLoading: Record<string, boolean>;
  loading: boolean;
  error: string | null;
};

const initialState: AnalyticsModelState = {
  analyticsModels: [],
  analyticsModelById: {},
  analyticsModelByIdLoading: {},
  loading: false,
  error: null,
};

export const fetchAnalyticsModels = createAsyncThunk(
  `${SliceName.analyticsModel}/fetchAll`,
  async (params: ListAnalyticsModelsParams) => {
    const response = await analyticsModelsApi.listAnalyticsModels({
      ...params,
      include_draft: params.include_draft ?? true,
      include_deleted: params.include_deleted ?? true,
    });
    return response.items;
  },
);

export const fetchAnalyticsModelById = createAsyncThunk(
  `${SliceName.analyticsModel}/fetchById`,
  async (id: string) => analyticsModelsApi.getAnalyticsModel(id),
);

export const createAnalyticsModel = createAsyncThunk(
  `${SliceName.analyticsModel}/create`,
  async (data: CreateAnalyticsModelRequest) =>
    analyticsModelsApi.createAnalyticsModel(data),
);

export const updateAnalyticsModel = createAsyncThunk(
  `${SliceName.analyticsModel}/update`,
  async ({ id, data }: { id: string; data: UpdateAnalyticsModelRequest }) =>
    analyticsModelsApi.updateAnalyticsModel(id, data),
);

export const deleteAnalyticsModel = createAsyncThunk(
  `${SliceName.analyticsModel}/delete`,
  async (id: string) => {
    await analyticsModelsApi.deleteAnalyticsModel(id);
    return id;
  },
);

export const restoreAnalyticsModel = createAsyncThunk(
  `${SliceName.analyticsModel}/restore`,
  async ({
    id,
    data,
  }: {
    id: string;
    data?: { code?: string; name?: string };
  }) => {
    await analyticsModelsApi.restoreAnalyticsModel(id, data);
    return id;
  },
);

export const cloneAnalyticsModel = createAsyncThunk(
  `${SliceName.analyticsModel}/clone`,
  async ({
    id,
    targetOrganizationId,
  }: {
    id: string;
    targetOrganizationId: string;
  }) =>
    analyticsModelsApi.cloneAnalyticsModel(id, {
      target_organization_id: targetOrganizationId,
    }),
);

const setStatusById = (
  state: AnalyticsModelState,
  id: string,
  status: VersionStatus,
) => {
  const index = state.analyticsModels.findIndex((model) => model.id === id);
  if (index !== -1) {
    state.analyticsModels[index] = {
      ...state.analyticsModels[index],
      status,
    };
  }

  const full = state.analyticsModelById[id];
  if (full) {
    state.analyticsModelById[id] = { ...full, status };
  }
};

const analyticsModelSlice = createSlice({
  name: SliceName.analyticsModel,
  initialState,
  reducers: {
    setAnalyticsModels: (
      state,
      action: PayloadAction<AnalyticsModelSummary[]>,
    ) => {
      state.analyticsModels = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalyticsModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsModels.fulfilled, (state, action) => {
        state.analyticsModels = action.payload;
        state.loading = false;
      })
      .addCase(fetchAnalyticsModels.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ?? 'Failed to fetch analytics models';
      })
      .addCase(fetchAnalyticsModelById.pending, (state, action) => {
        state.analyticsModelByIdLoading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsModelById.fulfilled, (state, action) => {
        state.analyticsModelByIdLoading[action.meta.arg] = false;
        state.analyticsModelById[action.payload.id] = action.payload;
      })
      .addCase(fetchAnalyticsModelById.rejected, (state, action) => {
        state.analyticsModelByIdLoading[action.meta.arg] = false;
        state.error = action.error.message ?? 'Failed to fetch analytics model';
      })
      .addCase(createAnalyticsModel.fulfilled, (state, action) => {
        state.analyticsModels.push(action.payload);
        state.analyticsModelById[action.payload.id] = action.payload;
      })
      .addCase(updateAnalyticsModel.fulfilled, (state, action) => {
        const index = state.analyticsModels.findIndex(
          (model) => model.id === action.payload.id,
        );
        if (index !== -1) {
          state.analyticsModels[index] = action.payload;
        }
        state.analyticsModelById[action.payload.id] = action.payload;
      })
      .addCase(deleteAnalyticsModel.fulfilled, (state, action) => {
        setStatusById(state, action.payload, 'deleted');
      })
      .addCase(restoreAnalyticsModel.fulfilled, (state, action) => {
        setStatusById(state, action.payload, 'active');
      });
  },
});

export const { actions } = analyticsModelSlice;

export default analyticsModelSlice.reducer;
